import { useEffect, useContext, useState, useRef } from 'react';
import { init, getInstanceByDom } from 'echarts';
import type { CSSProperties } from 'react';
import type { EChartsOption, ECharts, SetOptionOpts, EChartsType } from 'echarts';
import { ColorModeContext } from '@/modules/application';
import useEchartsResize from './hooks/useEchartsResize';
import { CHART_THEME } from '@/lib/echarts';

// Color name enum for better readability
export enum ChartColorName {
  BLUE = 'blue',
  GREEN = 'green',
  YELLOW = 'yellow',
  RED = 'red',
  CYAN = 'cyan',
  TEAL = 'teal',
  ORANGE = 'orange',
  PURPLE = 'purple',
  PINK = 'pink',
}

// Color maps for named access
const LIGHT_COLOR_MAP = {
  [ChartColorName.BLUE]: '#5470c6',
  [ChartColorName.GREEN]: '#91cc75',
  [ChartColorName.YELLOW]: '#fac858',
  [ChartColorName.RED]: '#ee6666',
  [ChartColorName.CYAN]: '#73c0de',
  [ChartColorName.TEAL]: '#3ba272',
  [ChartColorName.ORANGE]: '#fc8452',
  [ChartColorName.PURPLE]: '#9a60b4',
  [ChartColorName.PINK]: '#ea7ccc',
};

const LIGHT_COLORS = Object.values(LIGHT_COLOR_MAP);

const DARK_COLOR_MAP = {
  [ChartColorName.BLUE]: '#4992ff',
  [ChartColorName.GREEN]: '#7cffb2',
  [ChartColorName.YELLOW]: '#fddd60',
  [ChartColorName.RED]: '#ff6e76',
  [ChartColorName.CYAN]: '#58d9f9',
  [ChartColorName.TEAL]: '#05c091',
  [ChartColorName.ORANGE]: '#ff8a45',
  [ChartColorName.PURPLE]: '#8d48e3',
  [ChartColorName.PINK]: '#dd79ff',
};

const DARK_COLORS = Object.values(DARK_COLOR_MAP);

/**
 * Get the color palette based on the current theme
 */
export function getChartColors(isDarkMode: boolean): string[] {
  return isDarkMode ? DARK_COLORS : LIGHT_COLORS;
}

/**
 * Get a specific color from the chart palette by name
 */
export function getChartColorByName(colorName: ChartColorName, isDarkMode: boolean): string {
  const colorMap = isDarkMode ? DARK_COLOR_MAP : LIGHT_COLOR_MAP;
  return colorMap[colorName];
}

/**
 * Hook to get chart colors for the current theme
 */
export function useChartColors(): {
  getColorByName: (colorName: ChartColorName) => string;
  colorMap: Record<ChartColorName, string>;
  isDarkMode: boolean;
} {
  const { mode: theme } = useContext(ColorModeContext);
  const isDarkMode = theme === 'dark';

  return {
    getColorByName: (colorName: ChartColorName) => getChartColorByName(colorName, isDarkMode),
    colorMap: isDarkMode ? DARK_COLOR_MAP : LIGHT_COLOR_MAP,
    isDarkMode,
  };
}

export interface ReactEChartsProps {
  option: EChartsOption;
  style?: CSSProperties;
  settings?: SetOptionOpts;
  loading?: boolean;
  onChartReady?: (instance: ECharts) => void;
}

export function ReactECharts({ option, style, settings, loading, onChartReady }: ReactEChartsProps): React.JSX.Element {
  const chartRef = useRef<HTMLDivElement>(null);
  const { mode: theme } = useContext(ColorModeContext);

  const [echarts, setEcharts] = useState<EChartsType>();
  const containerDivRef = useEchartsResize(echarts);

  useEffect(() => {
    // Initialize chart
    let chart: ECharts | undefined;
    if (chartRef.current !== null) {
      chart = init(chartRef.current, theme === 'dark' ? CHART_THEME.dark : CHART_THEME.light);

      if (onChartReady) {
        onChartReady(chart);
      }
      setEcharts(chart);
    }

    // Return cleanup function
    return () => {
      chart?.dispose();
    };
  }, [theme]);

  useEffect(() => {
    // Update chart
    if (chartRef.current !== null) {
      const chart = getInstanceByDom(chartRef.current);
      if (chart === undefined) {
        return;
      }

      // Apply color palette based on theme
      const colorPalette = getChartColors(theme === 'dark');

      option.backgroundColor = 'transparent';
      option.color = colorPalette;

      chart.setOption(option, settings);
    }
  }, [option, settings, theme]); // Whenever theme changes we need to add option and setting due to it being deleted in cleanup function

  useEffect(() => {
    // Update chart
    if (chartRef.current !== null) {
      const chart = getInstanceByDom(chartRef.current);
      if (chart === undefined) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      loading === true ? chart.showLoading() : chart.hideLoading();
    }
  }, [loading, theme]);
  return (
    <div ref={containerDivRef} className="h-full w-full flex-1">
      <div ref={chartRef} style={{ width: '100%', height: '100%', ...style }} />
    </div>
  );
}
